import * as Sentry from '@sentry/browser'
import {
  addOperationNameToBreadcrumbs,
  shouldIgnoreError,
  stripPiiDataFromPayload
} from './helpers'

type SentryConfigProps = {
  environment: string // "checkout" | "team-controls" | "development" | "production" etc.
  merchantSlug: string
  merchantId: string
  userId: string
  userRole: string
  storeId: string
  storeSlug: string
}

const initSentry = ({
  environment,
  merchantSlug,
  merchantId,
  userId,
  userRole,
  storeId,
  storeSlug
}: SentryConfigProps) => {
  const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
      // [::1] is the IPv6 localhost address.
      window.location.hostname === '[::1]' ||
      // 127.0.0.1/8 is considered localhost for IPv4.
      window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
      )
  )
  // No Sentry tracking on local developments.
  if (isLocalhost) return

  // If Sentry is already initialised, return out.
  if (Sentry.getClient()) return

  // non-prod default config
  let replayConfig = {
    // Capture Replay for 0% of all sessions,
    // plus for 20% of sessions with an error
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.2
  }

  if (environment === 'production' || environment === 'prod') {
    // prod config
    replayConfig = {
      // Capture Replay for 5% of all sessions,
      // plus for 80% of sessions with an error
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 0.8
    }
  }

  // RELEASE_TAG on dev envs is the GIT_BRANCH value.
  const releaseTagVersion = process.env.REACT_APP_RELEASE_TAG

  const sentryRelease = releaseTagVersion || 'LOCAL'

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,

    release: `${sentryRelease}`,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        mask: [
          '.sentry-mask',
          '[data-sentry-mask]',

          'input[type="email"]',
          'input[type="password"]',
          'input[type="tel"]',

          'input[name="email"]',
          'input[name="password"]',
          'input[name="phone"]',
          'input[name="first_name"]',
          'input[name="last_name"]',
          'input[name="contact_num"]',
          'input[name="company_phone_number"]',
          'input[name="company_email"]',
          'input[name="cro_registered_number"]',
          'input[name="vat_id"]',
          'input[name="registered_company_name"]',
          'input[name="storeName"]',
          'input[name="merchant_name"]',
          'input[name="setting_email"]',
          'input[name="trust_pilot_email"]',
          'input[name="accounts_email"]',
          'input[name="current_email"]',

          'input[name="address"]',
          'input[name="address_line_1"]',
          'input[name="address_line_2"]',
          'input[name="address_city"]',
          'input[name="address_postcode"]',
          'input[name="address_country"]',
          'input[name="registered_company_address"]',

          'input[name*="customer_details_subform"]',
          'input[name*="drop_off_subform"]',

          'input[name*="email"]',
          'input[name*="phone"]',
          'input[name*="password"]',
          'input[name*="name"]',
          'input[name*="contact"]',
          'input[name*="address"]',
          'input[name*="company"]',
          'input[name*="merchant"]',
          'input[name*="registration"]',
          'input[name*="stripe"]',
          'input[name*="token"]',
          'input[name*="api_key"]',
          'input[name*="secret"]',
          'input[name*="vat"]',
          'input[name*="line_1"]',
          'input[name*="line_2"]',
          'input[name*="city"]',
          'input[name*="zip"]',
          'input[name*="postcode"]',
          'input[name*="country"]',

          'textarea[name*="email"]',
          'textarea[name*="name"]',
          'textarea[name*="address"]',
          'textarea[name*="company"]',
          'textarea[name*="contact"]',

          '[data-testid="emailInput"]',
          '[data-testid="passwordInput"]',
          '[data-testid="passwordConfirmationInput"]',
          '[data-testid="password-input"]',
          '[data-testid="onboarding-email-input"]',
          '[data-testid="onboarding-phone-input"]',
          '[data-testid="onboarding-first-name-input"]',
          '[data-testid="onboarding-last-name-input"]',
          '[data-testid="onboarding-merchant-name-input"]',
          '[data-testid="onboarding-merchant-email-input"]',
          '[data-testid="onboarding-registered-company-name-input"]',
          '[data-testid="general-settings-company-name-field"]',
          '[data-testid="general-settings-email-field"]',
          '[data-testid="registered-company-name-field"]',
          '[data-testid="company-phone-number-field"]',
          '[data-testid="company-registration-number-field"]',
          '[data-testid="accounts-email-field"]',
          '[data-testid="trust-pilot-email-field"]',
          '[data-testid="accountsEmail"]',

          '[data-testid*="email"]',
          '[data-testid*="phone"]',
          '[data-testid*="password"]',
          '[data-testid*="name"]',
          '[data-testid*="contact"]',
          '[data-testid*="address"]',
          '[data-testid*="company"]',
          '[data-testid*="merchant"]',
          '[data-testid*="business"]',
          '[data-testid*="registration"]',
          '[data-testid*="accounts"]',
          '[data-testid*="card"]',
          '[data-testid*="payment"]',
          '[data-testid*="vat"]',
          '[data-testid*="billing"]',

          '[class*="email"]',
          '[class*="name"]',
          '[class*="contact"]',
          '[class*="customer"]',
          '[class*="merchant"]',
          '[class*="company"]',
          '[class*="address"]',
          '[class*="card"]',
          '[class*="payment"]',

          '[class*="api-key"]',
          '[class*="token"]',
          '[class*="secret"]',
          '[class*="stripe"]',
          '[data-customer-id]',
          '[data-token]',
          '[data-api-key]',

          '[data-testid*="first-name"]',
          '[data-testid*="last-name"]',
          '[data-testid*="phone-number"]',
          '[data-testid*="contact-number"]',
          '[data-testid*="mobile"]',
          '[data-testid*="email-field"]',
          '[data-testid*="accounts-email"]',
          '[data-testid*="company-email"]',
          '[data-testid*="full-name"]',
          '[data-testid*="customer-name"]',
          '[data-testid*="recipient-name"]',
          '[data-testid*="postcode"]',
          '[data-testid*="zip"]',
          '[data-testid*="city"]',
          '[data-testid*="country"]',
          '[data-testid*="stripe"]',
          '[data-testid*="token"]',
          '[data-testid*="secret"]',
          '[data-testid*="api-key"]',
          '[data-testid*="key"]'
        ],
        maskAllText: false,
        blockAllMedia: true,
        maskAllInputs: true,
        networkDetailAllowUrls: [
          window.location.origin,
          'https://partner.api.slerp.com.team-controls.slerp.io/v1/graphql', // dev
          'https://partner.api.slerp.com/v1/graphq' // prod
        ],
        beforeAddRecordingEvent: (event) => stripPiiDataFromPayload(event)
      }),
      Sentry.httpClientIntegration(),
      Sentry.extraErrorDataIntegration({ depth: 5 })
    ],
    beforeBreadcrumb: (breadcrumb, hint) => {
      const expandedBreadcrumb = addOperationNameToBreadcrumbs(breadcrumb, hint)
      return expandedBreadcrumb
    },
    environment: environment,
    allowUrls: [`slerp.com`],
    beforeSend: (error, hint) => {
      return shouldIgnoreError(error, hint) ? null : error
    },

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^\//],
    ...replayConfig
  })

  Sentry.setContext('session', {
    slug: merchantSlug,
    merchantId: merchantId,
    userId: userId,
    userRole: userRole,
    storeId: storeId,
    storeSlug: storeSlug
  })
}

export { initSentry }
